import React, { useEffect, useRef } from 'react';
import { TextInput as NativeTextInput, Platform } from 'react-native';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { applyBrandFont } from '../../design/typography';
import { useIsOnCard } from '../../design/fieldSurface';

const Input = ({
	autoFocus,
	value,
	placeholder,
	onFocus,
	password,
	onChange,
	disabled,
	style = {},
	...passThroughProps
}) => {
	const styles = useThemedStyles(createStyles);
	const isOnCard = useIsOnCard();
	const isMultiline = Boolean(passThroughProps.multiline);
	const { theme } = useTheme();
	const ref = useRef();
	// auto enable keyboard for focused NativeTextInput
	useEffect(() => {
		if (autoFocus) {
			if (Platform.OS === 'ios') {
				ref.current?.focus();
			}
			else {
				setTimeout(() => ref.current?.focus(), 150);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<NativeTextInput
			ref={ref}
			{...passThroughProps}
			style={applyBrandFont([
				styles.input,
				isOnCard && styles.inputOnCard,
				isMultiline ? styles.multilineInput : styles.singleLineInput,
				disabled && styles.disabledInput,
				style,
			], theme.ui.areBrandFontsLoaded)}
			onChangeText={onChange}
			placeholder={placeholder}
			placeholderTextColor={theme.ui.colors.textSecondary}
			value={value}
			onFocus={onFocus}
			secureTextEntry={password}
			editable={!disabled}
		/>
	);
};

const createStyles = ({ ui: { colors, radius, touch, type } }) => ({
	input: {
		// No lineHeight: it shifts single-line input text on iOS.
		fontSize: type.rowText.fontSize,
		fontFamily: type.rowText.fontFamily,
		borderRadius: radius.card,
		color: colors.text,
		backgroundColor: colors.card,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		marginBottom: 10,
		paddingHorizontal: 14,
	},
	// Inside a Card the card-coloured fill would vanish (design/fieldSurface.js).
	inputOnCard: {
		backgroundColor: colors.canvas,
	},
	singleLineInput: {
		height: touch.min,
		paddingVertical: 0,
	},
	multilineInput: {
		minHeight: touch.min,
		paddingTop: 12,
		paddingBottom: 12,
		textAlignVertical: 'top',
	},
	// Keep the outline: disabledBg matches the card in Light.
	disabledInput: {
		color: colors.disabledText,
		backgroundColor: colors.disabledBg,
		borderColor: colors.borderStrong,
	},
});

export default Input;
